import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
    isSplitApplied,
} from './models/IQueryContext';

export class QueryContextHelper {
    /**
     * Get datasouce column
     * @param queryContext
     * @param columnId
     * @returns datasource column or undefined
     */
    static getDatasourceColumn = (
        queryContext: IQueryContext,
        columnId: DatasourceColumnId
    ): IQueryContextDatasourceColumn | undefined => {
        return queryContext.datasourceColumns.find(
            (col) => col.id === columnId
        );
    };
    /**
     * Get or create datasource column
     * @param queryContext
     * @param columnId
     * @returns datasource column
     */
    static getOrCreateDatasourceColumn = (
        queryContext: IQueryContext,
        columnId: DatasourceColumnId,
        emptyState: IQueryContextDatasourceColumn['state'] = {
            value: [],
            isFilterApplied: false,
            isSplitApplied: false,
        }
    ): IQueryContextDatasourceColumn => {
        let column = queryContext.datasourceColumns.find(
            (col) => col.id === columnId
        );
        // if undefined, create FIREBASE_COUNTRY column
        if (!column) {
            queryContext.datasourceColumns.push({
                id: columnId,
                state: emptyState,
            });
        }

        column = queryContext.datasourceColumns.find(
            (col) => col.id === columnId
        );
        if (!column) {
            throw new Error(`${columnId} column not found in queryContext`);
        }
        return column;
    };

    /** Keep the exclusive Group/Bucket state valid after a context change. */
    static normalizeDatasourceColumnState = (
        queryContext: IQueryContext,
        changedDatasourceColumn?: IQueryContextDatasourceColumn
    ): void => {
        const bucketColumns = queryContext.datasourceColumns.filter(
            (column) => column.state.isBucketApplied
        );
        const bucketOwnerId = changedDatasourceColumn?.state.isBucketApplied
            ? changedDatasourceColumn.id
            : bucketColumns[0]?.id;
        if (bucketOwnerId) {
            if (
                !changedDatasourceColumn?.state.isBucketApplied &&
                bucketColumns.length > 1
            ) {
                throw new Error(
                    `[QueryContextHelper] Multiple Group filters are active: ${bucketColumns
                        .map((column) => column.id)
                        .join(
                            ', '
                        )}. A saved view or context in code may contain only one Group filter.`
                );
            }
            queryContext.datasourceColumns = queryContext.datasourceColumns.map(
                (column) => {
                    if (!column.state.isBucketApplied) {
                        return column;
                    }
                    if (column.id !== bucketOwnerId) {
                        return {
                            ...column,
                            state: {
                                ...column.state,
                                isBucketApplied: false,
                            },
                        };
                    }
                    return {
                        ...column,
                        state: {
                            ...column.state,
                            isSplitApplied: false,
                        },
                    };
                }
            );
            if (changedDatasourceColumn?.state.isBucketApplied) {
                queryContext.datasourceColumns =
                    queryContext.datasourceColumns.filter(
                        (column) =>
                            column.state.isFilterApplied ||
                            column.state.isSplitApplied ||
                            column.state.isBucketApplied
                    );
            }
        }
    };

    /**
     * Returns split dimension keys when any split is applied in the query context, otherwise an empty array.
     * Currently assumes a single split dimension with key 'split_group' if a split is applied.
     */
    static getSplitDimensionKeys(queryContext: IQueryContext): string[] {
        return isSplitApplied(queryContext) ? ['split_group'] : [];
    }

    /**
     * Creates a "clean" row where only the datetime key and dimension keys
     * retain their values — every other column is set to `null`.
     * Useful for building gap-break rows for ECharts line charts.
     */
    static cleanRow(
        sourceRow: any,
        datetimeKey: string,
        dimensionKeys: string[] = []
    ): Record<string, any> {
        const preserveKeys = new Set([datetimeKey, ...dimensionKeys]);
        return Object.fromEntries(
            Object.keys(sourceRow).map((k) => [
                k,
                preserveKeys.has(k) ? sourceRow[k] : null,
            ])
        );
    }
}
